import { ThemeProvider } from '@mui/material/styles';
import { AuthProvider } from 'contexts/AuthContext';
import { DialogServiceProvider } from 'contexts/DialogProvider';
import SnackbarProvider from 'contexts/SnackbarProvider';
import Login from 'pages/auth/Login';
import Signup from 'pages/auth/Signup';
import DynamicTradeHistory from 'pages/dynamicTrade/DynamicTradeHistory';
import DynamicTradeManager from 'pages/dynamicTrade/DynamicTradeManager';
import ListDynamicTrade from 'pages/dynamicTrade/ListDynamicTrade';
import ProtectedRoute from 'parts/ProtectedRoute';
import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Header from './parts/Header';
import SideBar from './parts/SideBar';
import theme from './theme';

// Lazy loading the components
const OrderHistory = lazy(() => import('pages/orders/orderHistory'));
const LiveMonitoring = lazy(() => import('pages/dynamicTrade/LiveMonitoring'));
const PlaceOrder = lazy(() => import('pages/orders/placeOrder'));
const ExchangeMarket = lazy(() => import('./pages/ExchangeMarket'));
const LiveTrading = lazy(() => import('./pages/LiveTrading'));
const ListClient = lazy(() => import('./pages/clients/ListClient'));
const ListClientGroup = lazy(() => import('./pages/clients/ListClientGroup'));
const ListSymbolGroup = lazy(() => import('./pages/symbols/ListSymbolGroup'));
const RunCprScanner = lazy(() => import('pages/cprScanner/RunCprScanner'));
const ApiMetrics = lazy(() => import('pages/metrics/ApiMetrics'));
const LiveLogs = lazy(() => import('pages/administration/LiveLogs'));
const ExecutionList = lazy(() => import('pages/execution/ExecutionList'));
const Execution = lazy(() => import('pages/execution/Execution'));
const ExecutionTradeHistory = lazy(() => import('pages/execution/ExecutionTradeHistory'));
const CprBotList = lazy(() => import('pages/cpr/CprBotList'));
const Cpr = lazy(() => import('pages/cpr/CprWrapper'));
const CprTradeHistory = lazy(() => import('pages/cpr/CprTradeHistory'));

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <SnackbarProvider>
          <DialogServiceProvider>
            <Routes>
              {/* Public auth routes — no sidebar/header */}
              <Route path='/login' element={<Login />} />
              <Route path='/signup' element={<Signup />} />

              {/* All other routes — protected, inside main layout */}
              <Route
                path='/*'
                element={
                  <ProtectedRoute>
                    <div>
                      <section className="container-fluid d-sm-flex flex-row fixed-sidebar">
                        <SideBar />
                        <div className="wrapper pb-4">
                          <Header />
                          <div className="pt-3 p-sm-4 animation-element in-view">
                            <Suspense fallback={<div>Loading...</div>}>
                              <Routes>
                                <Route path='/' exact element={<Navigate replace to="/clients" />} />
                                <Route path='/liveTrading' exact element={<LiveTrading />} />
                                <Route path='/exchangeMarket' element={<ExchangeMarket />} />
                                <Route path='/clients' element={<ListClient />} />
                                <Route path='/clientGroups' element={<ListClientGroup />} />
                                <Route path='/symbolGroups' element={<ListSymbolGroup />} />
                                <Route path='/placeOrder' element={<PlaceOrder />} />
                                <Route path='/orderHistory' element={<OrderHistory />} />
                                <Route path='/dynamicTradeManager' element={<ListDynamicTrade />} />
                                <Route path='/dynamicTradeManager/add' element={<DynamicTradeManager />} />
                                <Route path='/dynamicTradeManager/edit/:id' element={<DynamicTradeManager />} />
                                <Route path='/dynamicTradeHistory' element={<DynamicTradeHistory />} />
                                <Route path='/liveMonitoring' element={<LiveMonitoring />} />
                                <Route path='/cpr' element={<CprBotList />} />
                                <Route path='/cpr/create' element={<Cpr />} />
                                <Route path='/cpr/bots/add' element={<Cpr />} />
                                <Route path='/cpr/:id/edit' element={<Cpr />} />
                                <Route path='/cpr/bots/:id/edit' element={<Cpr />} />
                                <Route path='/cpr/scan' element={<Cpr />} />
                                <Route path='/cprTradeHistory' element={<CprTradeHistory />} />
                                <Route path='/apiMetrics' element={<ApiMetrics />} />
                                <Route path='/liveLogs' element={<LiveLogs />} />
                                <Route path="/execution/bots" element={<ExecutionList />} />
                                <Route path="/execution/bots/add" element={<Execution />} />
                                <Route path="/execution/bots/:id/edit" element={<Execution />} />
                                <Route path="/execution/trade-history" element={<ExecutionTradeHistory />} />
                              </Routes>
                            </Suspense>
                          </div>
                        </div>
                      </section>
                    </div>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </DialogServiceProvider>
        </SnackbarProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
